import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Code, Loader2, ChevronRight, Download, Presentation } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import DataTable from '../components/DataTable';
import AssetGrid from '../components/AssetGrid';
import MessageVoting from '../components/MessageVoting';
import FeedbackModal from '../components/FeedbackModal';
import VoiceMicButton from '../components/VoiceMicButton';
import { reportData } from '../utils/buryReport';

const Chat = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [userVotes, setUserVotes] = useState({}); // Track user votes for each message
  const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, messageId: null });

  const [gammaLoading, setGammaLoading] = useState({}); // Track loading state for Gamma slides generation
  const [gammaProgress, setGammaProgress] = useState({}); // Track progress for Gamma slides generation

  const messagesEndRef = useRef(null);
  const hasReported = useRef(false); // 防止重复上报
  const initialPromptHandled = useRef(false);

  // 页面加载时上报路由访问（只执行一次）
  useEffect(() => {
    if (!hasReported.current) {
      reportData(1, 'Chat Page');
      hasReported.current = true;
    }
  }, []);

  // Preloaded prompt from Home ("I'm a {role}. What agents would you recommend?").
  // Defined-once via initialPromptHandled.current so re-renders / hot reload don't replay.
  useEffect(() => {
    if (initialPromptHandled.current) return;
    const incoming = location.state?.initialPrompt;
    if (!incoming) return;
    initialPromptHandled.current = true;
    if (location.state?.autoSend) {
      // sendMessage is defined later in this component; call by name via setTimeout
      // so we don't fight React's render order.
      setTimeout(() => sendMessage(incoming), 0);
    } else {
      setInput(incoming);
    }
    // Clear the state so a Back-Forward navigation doesn't replay it.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle user voting on messages
  const handleVote = async (messageId, vote) => {
    setUserVotes(prev => ({ ...prev, [messageId]: vote }));
    
    try {
      await axios.post('/api/feedback/vote', {
        messageId,
        vote,
        timestamp: new Date().toISOString()
      });
      
      if (vote === 'up') {
        toast.success('Thank you for your feedback!');
      }
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error('Failed to submit vote');
    }
  };

  // Handle feedback submission for thumbs down
  const handleFeedback = async (feedbackData) => {
    try {
      await axios.post('/api/feedback/submit', feedbackData);
      toast.success('Thank you for your detailed feedback!');
      closeFeedbackModal();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  // Open feedback modal
  const openFeedbackModal = (messageId, voteType) => {
    setFeedbackModal({ isOpen: true, messageId, voteType });
  };

  // Close feedback modal
  const closeFeedbackModal = () => {
    setFeedbackModal({ isOpen: false, messageId: null });
  };

  // Export data to CSV
  const exportToCSV = (data, columns, messageContent) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Create CSV header
      const header = columns.join(',');
      
      // Create CSV rows
      const rows = data.map(row => 
        columns.map(col => {
          const value = row[col];
          // Handle values that contain commas, quotes, or newlines
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      );
      
      // Combine header and rows
      const csvContent = [header, ...rows].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `chat-data-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Data exported to CSV successfully!');
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error('Failed to export data');
    }
  };

  // Gamma API integration for generating beautiful slides
  const generateGammaSlides = async (message, index) => {
    try {
      setGammaLoading(prev => ({ ...prev, [index]: true }));
      setGammaProgress(prev => ({ ...prev, [index]: { started: true } }));
      
      console.log('🎯 Starting Gamma slides generation for Chat...');
      
      // Calculate dynamic number of cards based on content length
      let inputText = '';
      
      if (message.content) {
        inputText += `## Summary\n${message.content}\n\n`;
      }
      
      if (message.sqlRationale) {
        inputText += `## SQL Analysis\n${message.sqlRationale}\n\n`;
      }
      
      if (message.data && message.data.length > 0) {
        inputText += `## Data Table\n`;
        if (message.columns) {
          inputText += `Columns: ${message.columns.join(', ')}\n\n`;
        }
        inputText += `Sample Data (first 5 rows):\n`;
        message.data.slice(0, 5).forEach((row, i) => {
          inputText += `Row ${i + 1}: ${Object.values(row).join(' | ')}\n`;
        });
        inputText += `\nTotal rows: ${message.data.length}\n\n`;
      }
      
      if (message.sqlQuery) {
        inputText += `## SQL Query\n${message.sqlQuery}\n\n`;
      }
      
      // Calculate dynamic number of cards based on word count (1 slide per 60 words)
      const textLength = inputText.length;
      const wordCount = inputText.trim().split(/\s+/).length;
      let numCards = Math.min(60, Math.max(3, Math.ceil(wordCount / 60))); // 1 slide per 60 words, minimum 3, maximum 60
      
      console.log(`📊 Calculated numCards: ${numCards} (based on ${wordCount} words, ${textLength} characters, capped at 60)`);
      console.log('📝 Input text for Gamma:', inputText.substring(0, 200) + '...');
      
      // Show slide count notification
      toast.success(`🎯 Generating ${numCards} slides... This may take a few minutes.`);
      
      // Start Gamma slides generation
      const response = await fetch('/api/chat/gamma-slides', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inputText: inputText,
          numCards: numCards
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to start Gamma slides generation');
      }

      const generationId = data.generationId;
      console.log('🆔 Gamma generation started with ID:', generationId);
      
      // Start progress tracking with 5-minute timer for large slide decks
      const startTime = Date.now();
      const maxWaitTime = 300 * 1000; // 5 minutes for large slide decks
      
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, maxWaitTime - elapsed);
        const progressPercent = Math.min(95, (elapsed / maxWaitTime) * 95); // Cap at 95% until actual completion
        
        setGammaProgress(prev => ({
          ...prev,
          [index]: {
            ...prev[index],
            started: true,
            elapsed: Math.floor(elapsed / 1000),
            remaining: Math.floor(remaining / 1000),
            progress: progressPercent
          }
        }));
      };
      
      // Update progress every second
      const progressInterval = setInterval(updateProgress, 1000);
      updateProgress(); // Initial update
      
      // Check status every 10 seconds - no max attempts, poll indefinitely
      let attempts = 0;
      
      const pollForCompletion = async () => {
        try {
          attempts++;
          console.log(`🔍 Polling attempt ${attempts} for generation ${generationId}`);
          
          const statusResponse = await fetch(`/api/chat/gamma-slides/${generationId}`);
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            
            if (statusData.success && statusData.status === 'completed') {
              clearInterval(progressInterval);
              clearInterval(pollInterval);
              
              setGammaProgress(prev => ({
                ...prev,
                [index]: { 
                  ...prev[index], 
                  completed: true,
                  progress: 100,
                  gammaUrl: statusData.gammaUrl,
                  exportUrl: statusData.exportUrl
                }
              }));
              
              setGammaLoading(prev => ({ ...prev, [index]: false }));
              toast.success('Gamma slides generated successfully!');
              
              // Update message with Gamma URLs
              setMessages(prev => prev.map((msg, i) => 
                i === index ? { ...msg, gammaUrl: statusData.gammaUrl, exportUrl: statusData.exportUrl } : msg
              ));
              return;
            }
          }
          
          // Keep polling indefinitely - no timeout
        } catch (error) {
          console.error('Error checking Gamma status:', error);
          // Keep polling even on error
        }
      };
      
      // Start polling immediately, then every 10 seconds
      pollForCompletion();
      const pollInterval = setInterval(pollForCompletion, 10000);
      
      // Keep polling indefinitely - no cleanup timeout
      // User can refresh page if needed, but we'll keep checking for completion
      
    } catch (error) {
      console.error('❌ Failed to generate Gamma slides:', error);
      toast.error('Failed to generate Gamma slides');
      setGammaLoading(prev => ({ ...prev, [index]: false }));
      setGammaProgress(prev => ({
        ...prev,
        [index]: { 
          ...prev[index], 
          completed: true, 
          error: error.message 
        }
      }));
    }
  };

  // Helper function to get export icon
  const getExportIcon = (url) => {
    if (!url) return '📄';
    if (url.includes('.pptx')) return '📊';
    if (url.includes('.pdf')) return '📄';
    return '📄';
  };

  // Clean up loading phase interval when component unmounts or loading stops
  useEffect(() => {
    return () => {
      // This will run when component unmounts
    };
  }, []);





  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;
    reportData(2, messageText);
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setFollowUpQuestions([]); // Clear previous follow-up questions
    setLoading(true);
    setLoadingPhase(0);

    // Start the 4-phase loading sequence
    let phaseInterval = setInterval(() => {
      setLoadingPhase(prev => {
        if (prev < 3) {
          return prev + 1;
        } else {
          clearInterval(phaseInterval);
          return prev;
        }
      });
    }, 10000); // 10 seconds per phase (40 seconds total)

    try {
      // Prepare conversation history (last 6 messages: 3 user + 3 bot exchanges)
      const conversationHistory = messages.slice(-6).map(msg => ({
        type: msg.type,
        content: msg.content,
        explanation: msg.content, // For bot messages, use content as explanation
        data: msg.data,
        sqlQuery: msg.sqlQuery
      }));

      const response = await axios.post('/api/chat/message', {
        message: messageText,
        conversationHistory: conversationHistory
      });

      // Check if we have a fallback message for data integration issues
      if (response.data.hasFallback && response.data.fallbackMessage) {
        const fallbackMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: response.data.fallbackMessage,
          error: false,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, fallbackMessage]);
        toast.info('Data integration in progress');
        return;
      }

      if (response.data.success) {
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: response.data.explanation,
          data: response.data.data,
          sqlQuery: response.data.sqlQuery,
          sqlRationale: response.data.sqlRationale,
          columns: response.data.columns,
          rowCount: response.data.rowCount,
          assetUrls: response.data.assetUrls || [],
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, botMessage]);
        
        // Generate follow-up questions based on the user's message and conversation context
        const followUps = await generateFollowUpQuestions(messageText, response.data.data, messages, response.data.sqlQuery, response.data.routing);
        setFollowUpQuestions(followUps);
        
        toast.success('Query executed successfully!');
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: response.data.message || 'Sorry, I couldn\'t process your request.',
          error: true,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, errorMessage]);
        toast.error('Failed to process query');
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        error: true,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
      toast.error('Network error occurred');
    } finally {
      setLoading(false);
      setLoadingPhase(0);
      if (phaseInterval) {
        clearInterval(phaseInterval);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const generateFollowUpQuestions = async (lastMessage, data, allMessages, sqlQuery, routing) => {
    const questions = [];
    
    try {
      // Step 1: Analyze the current query context using routing
      const queryContext = analyzeQueryContext(lastMessage, data, routing);
      
      // Step 2: Generate AI-powered follow-up questions
      const aiQuestions = await generateAIFollowUps(lastMessage, data, queryContext, allMessages);
      
      // Step 3: Add context-specific questions based on data analysis
      const contextQuestions = generateContextSpecificQuestions(data, queryContext);
      
      // Step 4: Add conversation flow questions
      const flowQuestions = generateConversationFlowQuestions(allMessages, queryContext);
      
      // Combine and prioritize questions
      const allQuestions = [...aiQuestions, ...contextQuestions, ...flowQuestions];
      
      // Remove duplicates and limit to 4 questions
      const uniqueQuestions = [...new Set(allQuestions)].slice(0, 4);
      
      return uniqueQuestions;
      
    } catch (error) {
      console.error('Error generating follow-up questions:', error);
      // Fallback to basic questions
      return generateFallbackQuestions(lastMessage, data);
    }
  };

  const analyzeQueryContext = (lastMessage, data, routing) => {
    const context = {
      category: routing?.category || 'General',
      primaryTables: routing?.primaryTables || [],
      secondaryTables: routing?.secondaryTables || [],
      keywords: routing?.keywords || [],
      confidence: routing?.confidence || 0,
      dataInsights: analyzeDataInsights(data),
      queryType: detectQueryType(lastMessage),
      timeContext: extractTimeContext(lastMessage),
      projectContext: extractProjectContext(lastMessage),
      hasResults: data && data.length > 0,
      resultCount: data ? data.length : 0,
      topValues: extractTopValues(data),
      anomalies: detectAnomalies(data)
    };
    
    return context;
  };

  const analyzeDataInsights = (data) => {
    if (!data || data.length === 0) return {};
    
    const insights = {
      hasProjects: false,
      hasFinancial: false,
      hasSafety: false,
      hasSchedule: false,
      hasQuality: false,
      hasResources: false,
      hasDocuments: false,
      hasRisk: false,
      projectTypes: new Set(),
      locations: new Set(),
      dateRanges: { min: null, max: null },
      numericColumns: [],
      categoricalColumns: [],
      highValues: {},
      lowValues: {},
      averages: {},
      totals: {}
    };

    // Analyze each row
    data.forEach(row => {
      Object.entries(row).forEach(([key, value]) => {
        const keyLower = key.toLowerCase();
        const valueStr = String(value).toLowerCase();
        
        // Detect data types
        if (typeof value === 'number' && !isNaN(value)) {
          insights.numericColumns.push(key);
          if (!insights.highValues[key] || value > insights.highValues[key]) {
            insights.highValues[key] = value;
          }
          if (!insights.lowValues[key] || value < insights.lowValues[key]) {
            insights.lowValues[key] = value;
          }
        } else if (typeof value === 'string' && value.length < 50) {
          insights.categoricalColumns.push(key);
        }
        
        // Detect categories
        if (keyLower.includes('project') || keyLower.includes('project_id') || keyLower.includes('project_name')) {
          insights.hasProjects = true;
        }
        if (keyLower.includes('budget') || keyLower.includes('cost') || keyLower.includes('spend') || keyLower.includes('revenue')) {
          insights.hasFinancial = true;
        }
        if (keyLower.includes('incident') || keyLower.includes('safety') || keyLower.includes('accident')) {
          insights.hasSafety = true;
        }
        if (keyLower.includes('schedule') || keyLower.includes('timeline') || keyLower.includes('progress')) {
          insights.hasSchedule = true;
        }
        if (keyLower.includes('punch') || keyLower.includes('inspection') || keyLower.includes('quality')) {
          insights.hasQuality = true;
        }
        if (keyLower.includes('equipment') || keyLower.includes('crew') || keyLower.includes('vendor')) {
          insights.hasResources = true;
        }
        if (keyLower.includes('document') || keyLower.includes('file') || keyLower.includes('photo')) {
          insights.hasDocuments = true;
        }
        if (keyLower.includes('risk') || keyLower.includes('issue') || keyLower.includes('problem')) {
          insights.hasRisk = true;
        }
        
        // Extract project types
        if (keyLower.includes('type') && valueStr) {
          insights.projectTypes.add(valueStr);
        }
        
        // Extract locations
        if (keyLower.includes('location') || keyLower.includes('city') || keyLower.includes('state')) {
          insights.locations.add(valueStr);
        }
        
        // Extract dates
        if (keyLower.includes('date') || keyLower.includes('created') || keyLower.includes('start') || keyLower.includes('end')) {
          const dateValue = new Date(value);
          if (!isNaN(dateValue)) {
            if (!insights.dateRanges.min || dateValue < insights.dateRanges.min) {
              insights.dateRanges.min = dateValue;
            }
            if (!insights.dateRanges.max || dateValue > insights.dateRanges.max) {
              insights.dateRanges.max = dateValue;
            }
          }
        }
      });
    });

    // Calculate averages and totals for numeric columns
    insights.numericColumns.forEach(col => {
      const values = data.map(row => row[col]).filter(v => typeof v === 'number' && !isNaN(v));
      if (values.length > 0) {
        insights.averages[col] = values.reduce((a, b) => a + b, 0) / values.length;
        insights.totals[col] = values.reduce((a, b) => a + b, 0);
      }
    });

    return insights;
  };

  const detectQueryType = (message) => {
    const messageLower = message.toLowerCase();
    
    if (messageLower.includes('compare') || messageLower.includes('vs') || messageLower.includes('versus')) {
      return 'comparison';
    }
    if (messageLower.includes('trend') || messageLower.includes('over time') || messageLower.includes('history')) {
      return 'trend';
    }
    if (messageLower.includes('top') || messageLower.includes('highest') || messageLower.includes('best')) {
      return 'ranking';
    }
    if (messageLower.includes('problem') || messageLower.includes('issue') || messageLower.includes('risk')) {
      return 'problem';
    }
    if (messageLower.includes('performance') || messageLower.includes('efficiency') || messageLower.includes('utilization')) {
      return 'performance';
    }
    if (messageLower.includes('budget') || messageLower.includes('cost') || messageLower.includes('financial')) {
      return 'financial';
    }
    if (messageLower.includes('schedule') || messageLower.includes('timeline') || messageLower.includes('progress')) {
      return 'schedule';
    }
    if (messageLower.includes('safety') || messageLower.includes('incident') || messageLower.includes('accident')) {
      return 'safety';
    }
    
    return 'general';
  };

  const extractTimeContext = (message) => {
    const messageLower = message.toLowerCase();
    
    if (messageLower.includes('this month') || messageLower.includes('current month')) {
      return 'current_month';
    }
    if (messageLower.includes('last month') || messageLower.includes('previous month')) {
      return 'previous_month';
    }
    if (messageLower.includes('this quarter') || messageLower.includes('current quarter')) {
      return 'current_quarter';
    }
    if (messageLower.includes('this year') || messageLower.includes('current year')) {
      return 'current_year';
    }
    if (messageLower.includes('last 30 days') || messageLower.includes('past month')) {
      return 'last_30_days';
    }
    if (messageLower.includes('last 90 days') || messageLower.includes('past quarter')) {
      return 'last_90_days';
    }
    
    return 'all_time';
  };

  const extractProjectContext = (message) => {
    const messageLower = message.toLowerCase();
    
    if (messageLower.includes('all projects') || messageLower.includes('every project')) {
      return 'all_projects';
    }
    if (messageLower.includes('active projects') || messageLower.includes('current projects')) {
      return 'active_projects';
    }
    if (messageLower.includes('completed projects') || messageLower.includes('finished projects')) {
      return 'completed_projects';
    }
    if (messageLower.includes('residential') || messageLower.includes('commercial')) {
      return 'project_type';
    }
    
    return 'general';
  };

  const extractTopValues = (data) => {
    if (!data || data.length === 0) return {};
    
    const topValues = {};
    const numericColumns = Object.keys(data[0]).filter(key => 
      typeof data[0][key] === 'number' && !isNaN(data[0][key])
    );
    
    numericColumns.forEach(col => {
      const sorted = [...data].sort((a, b) => b[col] - a[col]);
      topValues[col] = sorted.slice(0, 3).map(row => ({
        value: row[col],
        project: row.project_name || row.project_id || 'Unknown',
        otherData: Object.entries(row).filter(([k, v]) => k !== col && k !== 'project_name' && k !== 'project_id').slice(0, 3)
      }));
    });
    
    return topValues;
  };

  const detectAnomalies = (data) => {
    if (!data || data.length < 3) return {};
    
    const anomalies = {};
    const numericColumns = Object.keys(data[0]).filter(key => 
      typeof data[0][key] === 'number' && !isNaN(data[0][key])
    );
    
    numericColumns.forEach(col => {
      const values = data.map(row => row[col]).filter(v => typeof v === 'number' && !isNaN(v));
      if (values.length > 0) {
        const mean = values.reduce((a, b) => a + b, 0) / values.length;
        const stdDev = Math.sqrt(values.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / values.length);
        
        const outliers = data.filter(row => {
          const value = row[col];
          return typeof value === 'number' && !isNaN(value) && Math.abs(value - mean) > 2 * stdDev;
        });
        
        if (outliers.length > 0) {
          anomalies[col] = outliers.slice(0, 3);
        }
      }
    });
    
    return anomalies;
  };

  const generateAIFollowUps = async (lastMessage, data, context, allMessages) => {
    try {
      // Create a context summary for AI
      const contextSummary = {
        userQuestion: lastMessage,
        queryCategory: context.category,
        dataInsights: context.dataInsights,
        resultCount: context.resultCount,
        hasResults: context.hasResults,
        topValues: context.topValues,
        anomalies: context.anomalies,
        conversationHistory: allMessages.slice(-3).map(m => `${m.type}: ${m.content}`).join('\n')
      };

      // Call the backend to generate AI follow-ups
      const response = await axios.post('/api/chat/generate-followups', {
        context: contextSummary
      });

      if (response.data.success && response.data.followUps) {
        return response.data.followUps;
      }
    } catch (error) {
      console.error('Error generating AI follow-ups:', error);
    }
    
    return [];
  };

  const generateContextSpecificQuestions = (data, context) => {
    const questions = [];
    
    if (!context.hasResults) {
      questions.push(
        "Try a broader search with different criteria",
        "Check if there are any data issues for this time period",
        "Show me similar data from a different perspective"
      );
      return questions;
    }

    // Questions based on data insights
    if (context.dataInsights.hasFinancial) {
      if (Object.keys(context.anomalies).some(key => key.toLowerCase().includes('cost') || key.toLowerCase().includes('budget'))) {
        questions.push("Investigate the cost anomalies in the data");
      }
      if (context.dataInsights.averages.budget_utilization < 80) {
        questions.push("Show me projects with better budget utilization");
      }
    }

    if (context.dataInsights.hasSafety) {
      if (context.resultCount > 10) {
        questions.push("Focus on the most critical safety incidents");
      } else if (context.resultCount === 0) {
        questions.push("Check safety data for a different time period");
      }
    }

    if (context.dataInsights.hasSchedule) {
      if (Object.keys(context.anomalies).some(key => key.toLowerCase().includes('delay') || key.toLowerCase().includes('schedule'))) {
        questions.push("Analyze the schedule delays in detail");
      }
    }

    // Questions based on top values
    Object.entries(context.topValues).forEach(([column, topItems]) => {
      if (topItems.length > 0) {
        const columnName = column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        questions.push(`Drill down into the top ${columnName} projects`);
      }
    });

    // Questions based on project types
    if (context.dataInsights.projectTypes.size > 1) {
      questions.push("Compare performance across different project types");
    }

    // Questions based on locations
    if (context.dataInsights.locations.size > 1) {
      questions.push("Analyze performance by location");
    }

    return questions;
  };

  const generateConversationFlowQuestions = (allMessages, context) => {
    const questions = [];
    
    // If this is the first query, suggest exploration
    if (allMessages.length <= 2) {
      questions.push(
        "Show me an overview of all active projects",
        "What are the key performance metrics this month?"
      );
    }

    // If we've been discussing a specific category, suggest related topics
    if (context.category === 'Safety & Compliance') {
      questions.push(
        "Show me safety trends over the last 6 months",
        "Which projects have the best safety records?"
      );
    } else     if (context.category === 'Financial Management') {
      questions.push(
        "Show me the active project with highest RFI overdue",
        "Which active project have the most submittal"
      );
    } else if (context.category === 'Project Management') {
      questions.push(
        "Show me project completion rates",
        "Which projects are ahead of schedule?"
      );
    }

    // If we have time context, suggest temporal comparisons
    if (context.timeContext !== 'all_time') {
      questions.push(`Compare this ${context.timeContext.replace('_', ' ')} with the previous period`);
    }

    return questions;
  };

  const generateFallbackQuestions = (lastMessage, data) => {
    const messageLower = lastMessage.toLowerCase();
    
    if (messageLower.includes('project')) {
      return [
        "Show me project performance metrics",
        "Which projects need attention?",
        "Compare project efficiency across different types"
      ];
    }
    
    if (messageLower.includes('budget') || messageLower.includes('cost')) {
      return [
        "Show me the active project with highest RFI overdue",
        "Which active project have the most submittal",
        "Show me active project with most punchlist and incident"
      ];
    }
    
    if (messageLower.includes('safety') || messageLower.includes('incident')) {
      return [
        "Show me safety incident trends",
        "Which projects have the best safety records?",
        "Analyze safety performance by project type"
      ];
    }
    
    return [
      "Show me an overview of all active projects",
      "What are the key performance metrics?",
      "Which projects need attention?"
    ];
  };

  const formatSQL = (sql) => {
    return sql
      .replace(/SELECT/gi, '\nSELECT')
      .replace(/FROM/gi, '\nFROM')
      .replace(/WHERE/gi, '\nWHERE')
      .replace(/GROUP BY/gi, '\nGROUP BY')
      .replace(/ORDER BY/gi, '\nORDER BY')
      .replace(/LIMIT/gi, '\nLIMIT')
      .replace(/JOIN/gi, '\nJOIN')
      .replace(/AND/gi, '\n  AND')
      .replace(/OR/gi, '\n  OR')
      .replace(/CASE/gi, '\nCASE')
      .replace(/WHEN/gi, '\n  WHEN')
      .replace(/ELSE/gi, '\n  ELSE')
      .replace(/END/gi, '\nEND')
      .trim();
  };

  const formatMarkdown = (text) => {
    if (!text) return '';
    
    return text
      // Headers with emojis
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-gray-900 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold text-gray-900 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-gray-900 mb-4">$1</h1>')
      
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      
      // Lists
      .replace(/^\* (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
      .replace(/^- (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
      
      // Code blocks
      .replace(/```(.*?)```/gs, '<pre class="bg-gray-100 p-2 rounded text-sm font-mono overflow-x-auto my-2">$1</pre>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded text-sm font-mono">$1</code>')
      
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-sylvan-600 hover:text-sylvan-800 underline" target="_blank" rel="noopener noreferrer">$1</a>')
      
      // Note: Removed the problematic regex that was stripping dollar amounts
      
      // Line breaks
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br>')
      
      // Wrap in paragraph tags
      .replace(/^(.*)$/gm, '<p class="mb-2">$1</p>')
      .replace(/<p class="mb-2"><\/p>/g, '')
      .replace(/<p class="mb-2"><br><\/p>/g, '');
  };





  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col gap-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">AI Procore Analyst</h1>
                <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  auto-refresh nightly at 12 AM
                </span>
              </div>
              <p className="text-gray-600">AI-powered Procore construction operation data analysis and insights</p>
            </div>
          </div>
        </div>

        {/* Demo Callout */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" style={{display: 'none'}}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <div className="text-sm text-blue-700">
                <p>Current Procore is configured for Michigan projects only. <strong>Might extend to all projects soon!</strong></p>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Interface */}
        <div className="w-full flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[600px] flex flex-col">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-8">

              
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">Ask me specific questions about your construction data:</p>
                  <div className="grid grid-cols-1 gap-2 max-w-md mx-auto">
                    <button
                      onClick={() => sendMessage("Show me 25-480 project's RFI")}
                      className="text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
                    >
                      Show me 25-480 project's RFI
                    </button>
                    <button
                      onClick={() => sendMessage("Show me 25-480 project's Submittals")}
                      className="text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
                    >
                      Show me 25-480 project's Submittals
                    </button>
                    <button
                      onClick={() => sendMessage("Show me 25-480 project's Punchlists and incident")}
                      className="text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
                    >
                      Show me 25-480 project's Punchlists and incident
                    </button>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-4xl w-full rounded-lg px-4 py-3 ${
                      message.type === 'user'
                        ? 'bg-sylvan-600 text-white'
                        : message.error
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-start space-x-2">
                      {message.type === 'bot' && (
                        <Bot className="h-5 w-5 text-sylvan-600 mt-0.5 flex-shrink-0" />
                      )}
                      {message.type === 'user' && (
                        <User className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm break-words prose prose-sm max-w-none">
                          <div dangerouslySetInnerHTML={{ __html: formatMarkdown(message.content) }} />
                        </div>
                        
                        {/* SQL Query Display */}
                        {message.sqlQuery && (
                          <div className="mt-3">
                            <details className="bg-gray-800 rounded overflow-hidden">
                              <summary className="p-3 bg-gray-700 text-gray-300 cursor-pointer hover:bg-gray-600 transition-colors flex items-center justify-between">
                                <span className="text-sm font-medium">Generated SQL Query</span>
                                <Code className="h-4 w-4 flex-shrink-0" />
                              </summary>
                              <div className="p-3 text-green-400 text-xs font-mono">
                                <div className="overflow-x-auto">
                                  <pre className="whitespace-pre-wrap break-words">{formatSQL(message.sqlQuery)}</pre>
                                </div>
                              </div>
                            </details>
                          </div>
                        )}

                        {/* SQL Rationale Display */}
                        {message.sqlRationale && (
                          <div className="mt-3">
                            <details className="bg-sylvan-50 border border-sylvan-200 rounded overflow-hidden">
                              <summary className="p-3 bg-sylvan-100 text-sylvan-800 cursor-pointer hover:bg-sylvan-200 transition-colors flex items-center justify-between">
                                <span className="text-sm font-medium">SQL Analysis Explanation</span>
                                <ChevronRight className="h-4 w-4 flex-shrink-0 transform transition-transform group-open:rotate-90" />
                              </summary>
                              <div className="p-3 text-gray-700 text-sm">
                                <div className="prose prose-sm max-w-none">
                                  <div dangerouslySetInnerHTML={{ __html: formatMarkdown(message.sqlRationale) }} />
                                </div>
                              </div>
                            </details>
                          </div>
                        )}

                        {/* Data Results */}
                        {message.data && message.data.length > 0 && (
                          <div className="mt-3">
                            <div className="overflow-x-auto">
                              <DataTable 
                                data={message.data} 
                                columns={message.columns} 
                              />
                              
                              {/* Export Buttons - CSV and Slides */}
                              <div className="mt-3 flex justify-end space-x-2">
                                {/* CSV Export Button */}
                                <button
                                  onClick={() => exportToCSV(message.data, message.columns, message.content)}
                                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  title="Export data to CSV"
                                >
                                  <Download size={16} className="mr-2" />
                                  CSV
                                </button>
                                
                                {/* Slides Button - Show initially, then replace with Web/Download */}
                                {!gammaProgress[index] || !gammaProgress[index].completed ? (
                                  <button
                                    onClick={() => generateGammaSlides(message, index)}
                                    disabled={gammaLoading[index]}
                                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Generate beautiful slides with Gamma AI"
                                  >
                                    {gammaLoading[index] ? (
                                      <Loader2 size={16} className="mr-2 animate-spin" />
                                    ) : (
                                      <Presentation size={16} className="mr-2" />
                                    )}
                                    {gammaLoading[index] ? 'Generating...' : 'Slides'}
                                  </button>
                                ) : (
                                  // Show Web and Download buttons in place of Slides button
                                  <div className="flex space-x-2">
                                    {(gammaProgress[index]?.gammaUrl || message.gammaUrl) && (
                                      <button
                                        onClick={() => window.open(gammaProgress[index]?.gammaUrl || message.gammaUrl, '_blank')}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-sylvan-600 hover:bg-sylvan-700 rounded-md transition-colors"
                                        title="Open Gamma presentation in new tab"
                                      >
                                        🌐 Web
                                      </button>
                                    )}
                                    {(gammaProgress[index]?.exportUrl || message.exportUrl) && (
                                      <a
                                        href={gammaProgress[index]?.exportUrl || message.exportUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                                        title="Open PPTX in new tab"
                                      >
                                        📊 PPTX
                                      </a>
                                      )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Progress Display - Simple */}
                              {gammaLoading[index] && (
                                <div className="mt-2 text-sm text-gray-600">
                                  <div className="flex items-center justify-between mb-2">
                                    <span>⏱️ {gammaProgress[index]?.elapsed || 0}s elapsed</span>
                                    <span>⏳ {gammaProgress[index]?.remaining || 120}s remaining</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-sylvan-600 h-2 rounded-full transition-all duration-300 ease-out" 
                                      style={{ width: `${gammaProgress[index]?.progress || 0}%` }}
                                    ></div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Success Message */}
                              {gammaProgress[index]?.completed && !gammaLoading[index] && (
                                <div className="mt-2 text-center">
                                  <span className="text-sm text-green-600">✅ Slides ready! Check your downloads</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Asset Grid */}
                        {message.assetUrls && message.assetUrls.length > 0 && (
                          <AssetGrid 
                            assets={message.assetUrls}
                            title="Ad Assets with Performance"
                          />
                        )}

                        {/* Voting Component for Bot Messages */}
                        {message.type === 'bot' && (
                          <div className="mt-4 pt-3 border-t border-gray-200 mb-2">
                            <MessageVoting
                              messageId={message.id}
                              userVote={userVotes[message.id]}
                              onVote={handleVote}
                              onFeedback={openFeedbackModal}
                            />
                          </div>
                        )}

                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-600">
                          {loadingPhase === 0 && "Analyzing your question..."}
                          {loadingPhase === 1 && "Creating query..."}
                          {loadingPhase === 2 && "Getting response..."}
                          {loadingPhase === 3 && "Summarizing answer..."}
                        </span>
                        <div className="flex space-x-1 mt-1">
                          {[0, 1, 2, 3].map((phase) => (
                            <div
                              key={phase}
                              className={`h-1 w-8 rounded-full transition-all duration-300 ${
                                phase <= loadingPhase ? 'bg-sylvan-500' : 'bg-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            <div className="border-t border-gray-200 p-4">
              <form onSubmit={handleSubmit} className="flex space-x-3 items-center">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about your construction data from Procore and Vista tables..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
                <VoiceMicButton
                  getCurrentText={() => input}
                  onUpdate={(text) => setInput(text)}
                  className="p-2 border border-gray-200 rounded-lg"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="px-4 py-2 bg-sylvan-600 text-white rounded-lg hover:bg-sylvan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Send</span>
                </button>
              </form>
              
              {/* Follow-up Questions */}
              {followUpQuestions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100 mb-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <ChevronRight className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Follow-up questions:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {followUpQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => sendMessage(question)}
                        className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-xs font-medium transition-colors border border-blue-200"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={closeFeedbackModal}
        onSubmit={handleFeedback}
        messageId={feedbackModal.messageId}
        voteType={feedbackModal.voteType}
        userQuestion={messages.find(m => m.id === feedbackModal.messageId)?.content || ''}
      />
    </div>
  );
};

export default Chat; 